import { d, std } from 'typegpu';

export const det4 = (m0: d.v4f, m1: d.v4f, m2: d.v4f, m3: d.v4f) => {
  'use gpu';
  const d00 = m1.y * (m2.z * m3.w - m2.w * m3.z) - m1.z * (m2.y * m3.w - m2.w * m3.y) + m1.w * (m2.y * m3.z - m2.z * m3.y);
  const d01 = m1.x * (m2.z * m3.w - m2.w * m3.z) - m1.z * (m2.x * m3.w - m2.w * m3.x) + m1.w * (m2.x * m3.z - m2.z * m3.x);
  const d02 = m1.x * (m2.y * m3.w - m2.w * m3.y) - m1.y * (m2.x * m3.w - m2.w * m3.x) + m1.w * (m2.x * m3.y - m2.y * m3.x);
  const d03 = m1.x * (m2.y * m3.z - m2.z * m3.y) - m1.y * (m2.x * m3.z - m2.z * m3.x) + m1.z * (m2.x * m3.y - m2.y * m3.x);
  return m0.x * d00 - m0.y * d01 + m0.z * d02 - m0.w * d03;
};

export const scSolve4 = (m0: d.v4f, m1: d.v4f, m2: d.v4f, m3: d.v4f, f0: number, f1: number, f2: number, f3: number) => {
  'use gpu';
  const detVal = det4(m0, m1, m2, m3);
  const inv = 1.0 / detVal;
  const M0 = d.vec4f(f0, m0.y, m0.z, m0.w);
  const M1 = d.vec4f(m1.x, f1, m1.z, m1.w);
  const M2 = d.vec4f(m2.x, m2.y, f2, m2.w);
  const M3 = d.vec4f(m3.x, m3.y, m3.z, f3);
  return d.vec4f(det4(M0, m1, m2, m3) * inv, det4(m0, M1, m2, m3) * inv, det4(m0, m1, M2, m3) * inv, det4(m0, m1, m2, M3) * inv);
};

export const systemDeriv = (
  t0: number, w0: number, t1: number, w1: number,
  t2: number, w2: number, t3: number, w3: number,
  M0: number, L0: number, A0: number, R: number, N: number,
) => {
  'use gpu';
  const r2 = R * R;
  const r3 = r2 * R;
  const dd = L0 - A0;
  const m0 = M0;
  const m1 = std.select(0.0, M0 * R, N >= 2.0);
  const m2 = std.select(0.0, M0 * r2, N >= 3.0);
  const m3 = std.select(0.0, M0 * r3, N >= 4.0);
  const a0 = A0;
  const a1 = std.select(0.0, A0 * R, N >= 2.0);
  const a2 = std.select(0.0, A0 * r2, N >= 3.0);
  const a3 = std.select(0.0, A0 * r3, N >= 4.0);
  const b0 = dd;
  const b1 = std.select(0.0, dd * R, N >= 2.0);
  const b2 = std.select(0.0, dd * r2, N >= 3.0);
  const b3 = std.select(0.0, dd * r3, N >= 4.0);
  const abv0 = m1 + m2 + m3;
  const abv1 = m2 + m3;
  const abv2 = m3;
  const I0 = m0 * a0 * a0 + b0 * b0 * abv0;
  const I1 = std.select(1.0, m1 * a1 * a1 + b1 * b1 * abv1, N >= 2.0);
  const I2 = std.select(1.0, m2 * a2 * a2 + b2 * b2 * abv2, N >= 3.0);
  const I3 = std.select(1.0, m3 * a3 * a3, N >= 4.0);
  const mu0 = m0 * a0 - b0 * abv0;
  const mu1 = std.select(0.0, m1 * a1 - b1 * abv1, N >= 2.0);
  const mu2 = std.select(0.0, m2 * a2 - b2 * abv2, N >= 3.0);
  const mu3 = std.select(0.0, m3 * a3, N >= 4.0);

  const c01 = std.cos(t0 - t1);
  const c02 = std.cos(t0 - t2);
  const c03 = std.cos(t0 - t3);
  const c12 = std.cos(t1 - t2);
  const c13 = std.cos(t1 - t3);
  const c23 = std.cos(t2 - t3);
  const R0 = d.vec4f(I0, -b0 * mu1 * c01, -b0 * mu2 * c02, -b0 * mu3 * c03);
  const R1 = d.vec4f(-b0 * mu1 * c01, I1, -b1 * mu2 * c12, -b1 * mu3 * c13);
  const R2 = d.vec4f(-b0 * mu2 * c02, -b1 * mu2 * c12, I2, -b2 * mu3 * c23);
  const R3 = d.vec4f(-b0 * mu3 * c03, -b1 * mu3 * c13, -b2 * mu3 * c23, I3);

  const G = 9.81;
  const w0s = w0 * w0;
  const w1s = w1 * w1;
  const w2s = w2 * w2;
  const w3s = w3 * w3;
  const s01 = std.sin(t0 - t1);
  const s02 = std.sin(t0 - t2);
  const s03 = std.sin(t0 - t3);
  const s12 = std.sin(t1 - t2);
  const s13 = std.sin(t1 - t3);
  const s23 = std.sin(t2 - t3);
  const f0 = -G * mu0 * std.sin(t0) + b0 * (mu1 * s01 * w1s + mu2 * s02 * w2s + mu3 * s03 * w3s);
  const f1 = -G * mu1 * std.sin(t1) - b0 * mu1 * s01 * w0s + b1 * mu2 * s12 * w2s + b1 * mu3 * s13 * w3s;
  const f2 = -G * mu2 * std.sin(t2) - b0 * mu2 * s02 * w0s - b1 * mu2 * s12 * w1s + b2 * mu3 * s23 * w3s;
  const f3 = -G * mu3 * std.sin(t3) - b0 * mu3 * s03 * w0s - b1 * mu3 * s13 * w1s - b2 * mu3 * s23 * w2s;

  const al = scSolve4(R0, R1, R2, R3, f0, f1, f2, f3);
  return {
    dt0: w0, dw0: al.x,
    dt1: w1, dw1: al.y,
    dt2: w2, dw2: al.z,
    dt3: w3, dw3: al.w,
  };
};

export const sculptureDivergence = (
  bt0: number, bw0: number, bt1: number, bw1: number,
  bt2: number, bw2: number, bt3: number, bw3: number,
  pt0: number, pw0: number, pt1: number, pw1: number,
  pt2: number, pw2: number, pt3: number, pw3: number,
  N: number,
) => {
  'use gpu';
  const TWO_PI = 2.0 * std.acos(0);
  const TOL_LO = 0.05;
  const TOL_HI = 1.0;
  const OMEGA_SCALE = 2.0;
  const ratio = TOL_HI / TOL_LO;
  const invN = std.select(0.0, 1.0 / (N - 1.0), N > 1.0);
  let sum = 0.0;
  const g0 = std.select(0.0, 0.0, N > 1.0);
  const g1 = std.select(0.0, 1.0 * invN, N > 1.0);
  const g2 = std.select(0.0, 2.0 * invN, N > 1.0);
  const g3 = std.select(0.0, 3.0 * invN, N > 1.0);
  let da0 = bt0 - pt0;
  da0 = da0 - std.floor(da0 / TWO_PI + 0.5) * TWO_PI;
  let da1 = bt1 - pt1;
  da1 = da1 - std.floor(da1 / TWO_PI + 0.5) * TWO_PI;
  let da2 = bt2 - pt2;
  da2 = da2 - std.floor(da2 / TWO_PI + 0.5) * TWO_PI;
  let da3 = bt3 - pt3;
  da3 = da3 - std.floor(da3 / TWO_PI + 0.5) * TWO_PI;
  if (N >= 1.0) {
    const tolA = TOL_LO * std.exp(g0 * std.log(ratio));
    const tolW = tolA * OMEGA_SCALE;
    sum = sum + (da0 * da0) / (tolA * tolA) + (bw0 - pw0) * (bw0 - pw0) / (tolW * tolW);
  }
  if (N >= 2.0) {
    const tolA = TOL_LO * std.exp(g1 * std.log(ratio));
    const tolW = tolA * OMEGA_SCALE;
    sum = sum + (da1 * da1) / (tolA * tolA) + (bw1 - pw1) * (bw1 - pw1) / (tolW * tolW);
  }
  if (N >= 3.0) {
    const tolA = TOL_LO * std.exp(g2 * std.log(ratio));
    const tolW = tolA * OMEGA_SCALE;
    sum = sum + (da2 * da2) / (tolA * tolA) + (bw2 - pw2) * (bw2 - pw2) / (tolW * tolW);
  }
  if (N >= 4.0) {
    const tolA = TOL_LO * std.exp(g3 * std.log(ratio));
    const tolW = tolA * OMEGA_SCALE;
    sum = sum + (da3 * da3) / (tolA * tolA) + (bw3 - pw3) * (bw3 - pw3) / (tolW * tolW);
  }
  return sum;
};

export const computeSculptureTip = (
  t0: number, t1: number, t2: number, t3: number,
  A0: number, L0: number, R: number, N: number,
) => {
  'use gpu';
  const dd = L0 - A0;
  const b0 = dd;
  const b1 = dd * R;
  const b2 = dd * R * R;
  const a0 = A0;
  const a1 = A0 * R;
  const a2 = A0 * R * R;
  const a3 = A0 * R * R * R;
  const ax = std.select(0.0, -b0 * std.sin(t0), N >= 2.0)
    + std.select(0.0, -b1 * std.sin(t1), N >= 3.0)
    + std.select(0.0, -b2 * std.sin(t2), N >= 4.0);
  const ay = std.select(0.0, b0 * std.cos(t0), N >= 2.0)
    + std.select(0.0, b1 * std.cos(t1), N >= 3.0)
    + std.select(0.0, b2 * std.cos(t2), N >= 4.0);
  const la = std.select(std.select(std.select(a0, a1, N >= 2.0), a2, N >= 3.0), a3, N >= 4.0);
  const lt = std.select(std.select(std.select(t0, t1, N >= 2.0), t2, N >= 3.0), t3, N >= 4.0);
  return d.vec2f(ax + la * std.sin(lt), ay - la * std.cos(lt));
};